import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Typography,
    Row,
    Col,
    Tooltip,
    Popconfirm,
    Spin,
    Input,
    Modal
} from 'antd';
import {
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined
} from '@ant-design/icons';
import apiRequest from '../../hooks/ApiRequest';
import Layouts from '../../components/Layouts';
import { toast } from 'react-toastify';

const { Title } = Typography;

const EndorsementType = () => {
    const [loading, setLoading] = useState(false);
    const [endorsementTypes, setEndorsementTypes] = useState([]);
    const [filteredTypes, setFilteredTypes] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [formValues, setFormValues] = useState({ name: '' });

    useEffect(() => {
        fetchEndorsementTypes();
    }, []);

    const fetchEndorsementTypes = async () => {
        setLoading(true);
        try {
            const response = await apiRequest(
                `${process.env.REACT_APP_BASE_URL}/Policy/GetAllEndorsementType/EndorsementsType`,
                'GET'
            );
            if (response.success) {
                setEndorsementTypes(response.data.data);
                setFilteredTypes(response.data.data);
            } else {
                toast.error('Failed to fetch endorsement types');
            }
        } catch (error) {
            console.error('Error fetching endorsement types:', error);
            toast.error('Error fetching endorsement types');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        const lower = value.toLowerCase();
        const filtered = endorsementTypes.filter((o) =>
            [o.id, o.name, o.description]
                .map(v => String(v || '').toLowerCase())
                .some(s => s.includes(lower))
        );
        setFilteredTypes(filtered);
    };

    const showModal = (record = null) => {
        setCurrentRecord(record);
        setFormValues({ name: record?.name || '' });
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setFormValues({ name: '' });
        setCurrentRecord(null);
    };

    const handleSubmit = async () => {
        if (!formValues.name.trim()) {
            toast.error('Name is required');
            return;
        }

        const payload = {
            name: formValues.name.trim(),
            id: currentRecord ? currentRecord.id : 0
        };

        setLoading(true);
        const url = currentRecord
            ? `${process.env.REACT_APP_BASE_URL}/Policy/UpdateEndorsementType`
            : `${process.env.REACT_APP_BASE_URL}/Policy/CreateEndorsementType/EndorsementsType`;
        const method = 'POST';

        try {
            const response = await apiRequest(url, method, payload);
            if (response.success) {
                toast.success(`Endorsement type ${currentRecord ? 'updated' : 'created'} successfully`);
                fetchEndorsementTypes();
                handleCancel();
            } else {
                toast.error(response.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Error processing your request');
        } finally {
            setLoading(false);
        }
    };

    const deleteEndorsementType = async (id) => {
        setLoading(true);
        try {
            const response = await apiRequest(
                `${process.env.REACT_APP_BASE_URL}/Policy/DeleteEndorsementType/EndorsementsType/${id}`,
                'POST'
            );

            if (response.success) {
                toast.success('Endorsement type deleted successfully');
                fetchEndorsementTypes();
            } else {
                toast.error(response.message || 'Failed to delete endorsement type');
            }
        } catch (error) {
            console.error('Error deleting endorsement type:', error);
            toast.error('Error deleting endorsement type');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <>
                    <Tooltip title="Edit">
                        <Button type='primary' onClick={() => showModal(record)} >
                            <EditOutlined />
                        </Button>
                    </Tooltip>
                    <Popconfirm
                        title="Are you sure to delete this endorsement type?"
                        onConfirm={() => deleteEndorsementType(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Tooltip title="Delete">
                            <Button type="danger" style={{ marginLeft: 8 }}>
                                <DeleteOutlined />
                            </Button>
                        </Tooltip>
                    </Popconfirm>
                </>
            )
        }
    ];

    return (
        <Layouts>
            <div className="dash-bg-white" style={{ padding: '1rem' }}>
                <Row gutter={[16, 16]} justify="space-between" align="middle">
                    <Col xs={24} md={12}>
                        <Title level={4} style={{ color: '#0C1362', fontWeight: 600, margin: 0 }}>
                            Endorsement Types
                        </Title>
                    </Col>
                    <Col xs={24} md={12}>
                        <Row gutter={[8, 8]} justify="end">
                            <Col xs={24} sm={16} md={14}>
                                <Input.Search
                                    placeholder="Search types"
                                    allowClear
                                    // enterButton={<SearchOutlined />}
                                    enterButton
                                    value={searchText}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    onSearch={handleSearch}
                                    style={{ width: '100%' }}
                                />
                            </Col>
                            <Col xs={24} sm={10}>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => showModal()}
                                    block
                                >
                                    Add Type
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <div style={{ marginTop: '1rem' }}>
                    {loading ? (
                        <Spin size="large" style={{ display: 'block', margin: '2rem auto' }} />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={filteredTypes}
                            rowKey="id"
                            className="dashboard-table"
                            scroll={{ x: true }}
                        />
                    )}
                </div>

                <Modal
                    title={currentRecord ? 'Edit Endorsement Type' : 'Add New Endorsement Type'}
                    visible={isModalVisible}
                    onCancel={handleCancel}
                    footer={[
                        <Button key="cancel" onClick={handleCancel}>
                            Cancel
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            onClick={handleSubmit}
                            loading={loading}
                        >
                            {currentRecord ? 'Save' : 'Add'}
                        </Button>
                    ]}
                    destroyOnClose
                >
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ fontWeight: 500 }}>Name</label>
                        <Input
                            value={formValues.name}
                            placeholder="Enter endorsement type name"
                            onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                        />
                    </div>
                </Modal>
            </div>
        </Layouts>
    );
};

export default EndorsementType;
